import * as React from "react";
import {
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  Divider,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Alert,
  Box,
  TableContainer,
  Card,
  CardContent,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { adminFetch } from "../../services/adminFetch";

type Row = any;

export default function AdminPropertiesPage() {
  const nav = useNavigate();
  const { locale } = useParams<{ locale?: string }>();
  const currentLocale = locale || "el";

  const [q, setQ] = React.useState("");
  const [items, setItems] = React.useState<Row[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState<string | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const load = React.useCallback(async () => {
    setErr(null);
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (q.trim()) qs.set("q", q.trim());
      qs.set("limit", "50");
      qs.set("offset", "0");

      const res = await adminFetch(`/api/admin/properties?${qs.toString()}`);
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setErr(data?.error || "Αποτυχία φόρτωσης.");
        setItems([]);
        return;
      }

      setItems(Array.isArray(data.items) ? data.items : []);
    } catch {
      setErr("Αποτυχία φόρτωσης.");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [q]);

  React.useEffect(() => {
    load();
  }, [load]);

  return (
    <Stack spacing={2}>
      {/* Header */}
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        alignItems={{ xs: "stretch", md: "center" }}
        gap={2}
      >
        <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
          Admin · Properties
        </Typography>

        <Button
          fullWidth={isMobile}
          variant="contained"
          sx={{ fontWeight: 900, whiteSpace: "nowrap" }}
          onClick={() => nav(`/${currentLocale}/admin/new-property`)}
        >
          + Add property
        </Button>
      </Stack>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
        {/* Search */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={2}
          alignItems={{ md: "center" }}
        >
          <TextField
            label="Search (title / slug / excerpt)"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            fullWidth
            size="small"
          />

          <Button
            variant="outlined"
            onClick={load}
            fullWidth={isMobile}
            sx={{ whiteSpace: "nowrap" }}
          >
            Search
          </Button>
        </Stack>

        <Divider sx={{ my: 2 }} />

        {err ? <Alert severity="error">{err}</Alert> : null}

        {loading ? (
          <Typography sx={{ py: 2 }}>Φόρτωση...</Typography>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Σύνολο: <b>{items.length}</b>
            </Typography>

            {/* MOBILE LIST */}
            {isMobile ? (
              <Stack spacing={1.5}>
                {items.map((r: any) => (
                  <Card
                    key={r.id}
                    variant="outlined"
                    sx={{ borderRadius: 3, overflow: "hidden" }}
                  >
                    <CardContent sx={{ p: 1.5 }}>
                      <Stack spacing={1}>
                        <Box>
                          <Typography sx={{ fontWeight: 900 }}>
                            {r.display_title || "-"}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ wordBreak: "break-word" }}
                          >
                            #{r.id} · {r.slug || "-"}
                          </Typography>
                        </Box>

                        <Stack
                          direction="row"
                          spacing={1}
                          useFlexGap
                          flexWrap="wrap"
                          alignItems="center"
                        >
                          <Chip
                            size="small"
                            label={r.status || "?"}
                            variant="outlined"
                          />
                          {r.listing_status ? (
                            <Chip
                              size="small"
                              label={r.listing_status}
                              variant="outlined"
                            />
                          ) : null}

                          <Chip
                            size="small"
                            label={`Άτομα: ${r.persons_min ?? "-"}–${r.persons_max ?? "-"
                              }`}
                            variant="outlined"
                          />

                          <Chip
                            size="small"
                            label={
                              r.price_per_night != null
                                ? `€/night: ${r.price_per_night}€`
                                : "€/night: -"
                            }
                            variant="outlined"
                          />
                        </Stack>

                        <Stack direction="row" spacing={1}>
                          <Button
                            fullWidth
                            size="small"
                            variant="outlined"
                            onClick={() =>
                              nav(`/${currentLocale}/admin/properties/${r.id}`)
                            }
                          >
                            Edit
                          </Button>
                          <Button
                            fullWidth
                            size="small"
                            variant="text"
                            onClick={() =>
                              nav(`/${currentLocale}/properties/${r.slug}`)
                            }
                          >
                            View
                          </Button>
                        </Stack>
                      </Stack>
                    </CardContent>
                  </Card>
                ))}

                {!items.length ? (
                  <Paper
                    variant="outlined"
                    sx={{ p: 2, borderRadius: 3 }}
                  >
                    <Typography color="text.secondary">
                      Δεν υπάρχουν ακίνητα.
                    </Typography>
                  </Paper>
                ) : null}
              </Stack>
            ) : (
              /* DESKTOP TABLE */
              <TableContainer sx={{ overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 720 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Ακίνητο</TableCell>
                      <TableCell>Κατάσταση</TableCell>
                      <TableCell>Άτομα</TableCell>
                      <TableCell>€/night</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {items.map((r: any) => (
                      <TableRow key={r.id} hover>
                        <TableCell>
                          <Typography sx={{ fontWeight: 800 }}>
                            {r.display_title || "-"}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            #{r.id} · {r.slug}
                          </Typography>
                        </TableCell>

                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip
                              size="small"
                              label={r.status || "?"}
                              variant="outlined"
                            />
                            {r.listing_status ? (
                              <Chip
                                size="small"
                                label={r.listing_status}
                                variant="outlined"
                              />
                            ) : null}
                          </Stack>
                        </TableCell>

                        <TableCell>
                          {r.persons_min ?? "-"}–{r.persons_max ?? "-"}
                        </TableCell>

                        <TableCell>
                          {r.price_per_night != null ? (
                            <Chip
                              size="small"
                              label={`${r.price_per_night}€`}
                              variant="outlined"
                            />
                          ) : (
                            "-"
                          )}
                        </TableCell>

                        <TableCell align="right">
                          <Stack
                            direction="row"
                            spacing={1}
                            justifyContent="flex-end"
                          >
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() =>
                                nav(
                                  `/${currentLocale}/admin/properties/${r.id}`
                                )
                              }
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() =>
                                nav(`/${currentLocale}/properties/${r.slug}`)
                              }
                            >
                              View
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}

                    {!items.length ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography color="text.secondary">
                            Δεν υπάρχουν ακίνητα.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : null}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </>
        )}
      </Paper>
    </Stack>
  );
}
